export const isValidLatitude = (value) => {
  const n = parseFloat(value);
  return !Number.isNaN(n) && n >= -90 && n <= 90;
};

export const isValidLongitude = (value) => {
  const n = parseFloat(value);
  return !Number.isNaN(n) && n >= -180 && n <= 180;
};

export const parseGoogleMapsLink = (mapLink) => {
  if (!mapLink || typeof mapLink !== 'string') return null;
  const url = mapLink.trim();

  // Match raw coordinates like "25.2854, 51.531" or "25.2854,51.531"
  const rawCoordsMatch = url.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
  if (rawCoordsMatch && isValidLatitude(rawCoordsMatch[1]) && isValidLongitude(rawCoordsMatch[2])) {
    return { lat: rawCoordsMatch[1], lng: rawCoordsMatch[2] };
  }

  const atMatch = url.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (atMatch && isValidLatitude(atMatch[1]) && isValidLongitude(atMatch[2])) {
    return { lat: atMatch[1], lng: atMatch[2] };
  }

  const qMatch = url.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (qMatch && isValidLatitude(qMatch[1]) && isValidLongitude(qMatch[2])) {
    return { lat: qMatch[1], lng: qMatch[2] };
  }

  const placeMatch = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (placeMatch && isValidLatitude(placeMatch[1]) && isValidLongitude(placeMatch[2])) {
    return { lat: placeMatch[1], lng: placeMatch[2] };
  }

  return null;
};

export const buildGoogleMapLink = (lat, lng) =>
  `https://www.google.com/maps?q=${lat},${lng}`;

export const isShortMapLink = (mapLink = '') => {
  const value = String(mapLink || '').toLowerCase();
  return value.includes('maps.app.goo.gl') || value.includes('goo.gl/maps') || /(^|\/\/)goo\.gl\//.test(value);
};

export const normalizeMapLink = (mapLink) => {
  const raw = String(mapLink || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^(maps\.app\.goo\.gl|goo\.gl|google\.com|maps\.google\.com|www\.google\.com)/i.test(raw)) {
    return `https://${raw.replace(/^\/+/, '')}`;
  }
  return raw;
};

/** Prefer coordinates over pasted links so Maps opens reliably in the browser. */
export const resolveMapUrl = ({ mapLink, lat, lng } = {}) => {
  const latStr = String(lat || '').trim();
  const lngStr = String(lng || '').trim();
  if (isValidLatitude(latStr) && isValidLongitude(lngStr)) {
    return buildGoogleMapLink(latStr, lngStr);
  }

  const parsed = parseGoogleMapsLink(mapLink);
  if (parsed) {
    return buildGoogleMapLink(parsed.lat, parsed.lng);
  }

  const url = normalizeMapLink(mapLink);
  if (!url || !/^https?:\/\//i.test(url) || isShortMapLink(url)) {
    return null;
  }

  return url;
};

export const openExternalUrl = (url) => {
  const normalized = String(url || '').trim();
  if (!normalized) return false;

  const anchor = document.createElement('a');
  anchor.href = normalized;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  return true;
};

export const resolveCoordinates = ({ lat, lng, mapLink }) => {
  if (isValidLatitude(lat) && isValidLongitude(lng)) {
    return { lat: String(lat).trim(), lng: String(lng).trim() };
  }
  if (mapLink) {
    const parsed = parseGoogleMapsLink(mapLink);
    if (parsed) return parsed;
  }
  return null;
};
